import { ApplicationRef, Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TA_GOOGLE_MAPS_THEME } from '@temerity-analytics/ngx-ta-maps';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import { selectUserPreferences } from '../../auth/store/auth.selectors';
import { UserPreferencesDTO } from '../../auth/utility/auth.models';
import { Loadable } from '../../shared/loading-state/loadable';
import { AppState } from '../../store/app.reducers';

export enum Theme {
  LIGHT = 'light',
  DARK = 'dark',
  OBSIDIAN = 'obsidian',
}

export const ZEVA_THEME: {
  [key in Theme]: {
    name: string;
    scheme: 'light' | 'dark';
    bodyClass: string;
  }
} = {
  light: { bodyClass: 'light', name: 'Classic (Light)', scheme: 'light', },
  dark: { bodyClass: 'dark', name: 'Classic (Dark)', scheme: 'dark' },
  obsidian: { bodyClass: 'obsidian', name: 'Obsidian', scheme: 'dark' },
};

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly theme$ = new BehaviorSubject<Theme>(Theme.LIGHT);
  currentTheme$ = this.theme$.asObservable();

  get isDarkMode(): boolean {
    return ZEVA_THEME[this.currentTheme].scheme === 'dark';
  }
  readonly isDarkMode$ = this.currentTheme$.pipe(map(() => this.isDarkMode));

  private _autoThemeListener = (e: MediaQueryListEvent) => {
    const turnOn = e.matches;
    this.setTheme(turnOn ? Theme.DARK : Theme.LIGHT);
  };

  constructor(
    private readonly ref: ApplicationRef,
    private readonly store: Store<AppState>,
    @Inject(TA_GOOGLE_MAPS_THEME)
    private readonly mapTheme: BehaviorSubject<string>
  ) {}

  setTheme(theme: Theme): void {
    const preset = ZEVA_THEME[theme];
    if (!preset) {
      console.warn(`Received invalid theme key "${theme}". Falling back to "auto"`);
      this.setAuto();
      return;
    }

    const currentClass = document.body.className.split(' ').find(c => c.startsWith('theme--'));
    if (currentClass) document.body.classList.remove(currentClass);
    document.body.classList.add('theme--' + preset.bodyClass);
    window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', this._autoThemeListener);
    this.mapTheme.next(preset.bodyClass);
    
    this.theme$.next(theme);
  }

  // Returns an observable for the user's appearance preferences and sets the theme accordingly
  getTheme(): Observable<Loadable<UserPreferencesDTO | null>> {
    const listenUserPreferences$ = this.store.select(selectUserPreferences).pipe(
      tap((preferences: Loadable<UserPreferencesDTO | null>) => {
        if (!preferences.value?.appearance || preferences.value.appearance === 'auto') this.setAuto();
        else this.setTheme(preferences.value.appearance as Theme);
      })
    );
    return listenUserPreferences$;
  }

  // Returns the current theme
  get currentTheme(): Theme {
    return this.theme$.value;
  }

  // Set the theme based on the user's system preferences
  setAuto() {
    // determine which theme to use depending on user's browser/system theme preference
    const darkModeOn =
      window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

    if (darkModeOn) this.setTheme(Theme.DARK);
    else this.setTheme(Theme.LIGHT);

    // Listen for changes of the preference
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', this._autoThemeListener);
  }
}
